<template>
    <div
        class="user-timeline"
        style="display: flex; position: relative;"
    >
        <!-- Week grid background (keeps alignment with the rest of the calendar) -->
        <div
            v-for="(week, index) in weeks"
            :key="index"
            class="week-item"
            :style="{ width: weekColWidth + 'px' }"
        >
            -
        </div>
        <!-- Bars layer: absolutely positioned over the grid and clipped to the row -->
        <div
            class="bars-layer"
            :class="{ 'resize': isResizeMode, 'dragging': isDragMode }"
            @mousemove="containerMouseMove"
            @mouseup="containerMouseUp"
            @mousedown="containerMouseDown"
            @mouseleave="containerMouseOut"
        >
            <!-- One bar per schedule for this user -->
            <div
                v-for="(bar, barIndex) in renderedBars"
                :key="bar.id"
                class="schedule-bar"
                :style="{ left: bar.left + 'px', width: bar.width + 'px', background: color, zIndex: 4 + barIndex }"
                :title="bar.title"
            >
                <span class="start-date-text">{{ bar.startText }}</span>
                <span class="end-date-text">{{ bar.endText }}</span>
            </div>
        </div>
    </div>
</template>

<script>
import {timeUtils} from "@/components/mixins/TimeUtils";

export default {
    name: 'UserDutyOfficerTimeline',
    mixins: [timeUtils],
    props: {
        schedules: {
            type: Array,
            default() {
                return []
            }
        },
        timeColWidth: {
            type: Number,
            default: 100
        },
        calendarStartDate: {
            type: Date,
            default: null
        },
        visibleWeeksLimit: {
            type: Number,
            default: 20
        },
        calendarStartWeek: {
            type: Number,
            default: 10
        },
        color: {
            type: String,
            default: '#052f5b'
        }
    },
    emits: ['dates-changed'],
    data: function () {
        return {
            NONE: 0,
            DRAG: 1,
            RESIZE_START: 2,
            RESIZE_END: 3,
            weeks: [],
            hoverMode: 0,
            activeId: null,       // id of the bar being dragged/resized
            dragging: false,
            resizing: false,
            lastX: 0,
            datesUpdated: false,
            // Live pixel geometry override for the active bar while interacting
            override: null        // { id, left, width }
        }
    },
    computed: {
        weekColWidth() {
            return Math.round(this.timeColWidth / 7) * 7
        },
        isResizeMode() {
            return this.hoverMode === this.RESIZE_START || this.hoverMode === this.RESIZE_END
        },
        isDragMode() {
            return this.hoverMode === this.DRAG
        },
        // Base geometry per schedule (same math as the original TimeLineItem)
        bars() {
            if (!this.calendarStartDate) {
                return []
            }
            const dayMovement = this.weekColWidth / 7
            const startMs = new Date(this.calendarStartDate).getTime()
            const result = []
            for (const schedule of this.schedules) {
                if (!schedule.start_date) {
                    continue
                }
                const start = this.getDateFromString(schedule.start_date)
                const end = schedule.end_date ? this.getDateFromString(schedule.end_date) : null
                if (!start) {
                    continue
                }
                const diffDays = Math.floor((start.getTime() - startMs) / (1000 * 60 * 60 * 24))
                const left = diffDays * dayMovement
                let width
                if (end) {
                    const durationDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
                    width = durationDays * dayMovement
                } else {
                    const totalVisibleDays = this.weeks.length * 7
                    width = (totalVisibleDays - diffDays) * dayMovement
                }
                result.push({
                    id: schedule.id,
                    left,
                    width: Math.max(width, dayMovement),
                    title: this.barTitle(schedule),
                    startMs: start.getTime()
                })
            }
            // Sort by start date ascending so the latest-starting bar is painted last (on top)
            result.sort((a, b) => a.startMs - b.startMs)
            return result
        },
        // Bars with the live override applied to the active bar, plus date labels
        renderedBars() {
            return this.bars.map(bar => {
                const geo = (this.override && this.override.id === bar.id)
                    ? { left: this.override.left, width: this.override.width }
                    : { left: bar.left, width: bar.width }
                const { start, end } = this.datesForGeometry(geo.left, geo.width)
                return {
                    id: bar.id,
                    left: geo.left,
                    width: geo.width,
                    title: bar.title,
                    startText: start ? this.getShortDateWithoutTimezone(start.toISOString()) : '',
                    endText: end ? this.getShortDateWithoutTimezone(end.toISOString()) : ''
                }
            })
        }
    },
    watch: {
        calendarStartWeek: function () {
            this.initTable()
        },
        visibleWeeksLimit: function () {
            this.initTable()
        },
        schedules: function () {
            // Parent delivered fresh data (post-save) -> drop the live override so the
            // bar renders from the authoritative schedules. No snap-back because the
            // override was kept until exactly this moment.
            this.override = null
            this.activeId = null
        }
    },
    mounted: function () {
        this.initTable()
    },
    methods: {
        initTable() {
            this.weeks = []
            let startWeek = this.calendarStartWeek
            for (let i = 0; i < this.visibleWeeksLimit; i++) {
                this.weeks.push(this.$t('calendar.wk_prefix') + (startWeek + i))
            }
        },
        barTitle(schedule) {
            const start = schedule.start_date ? this.toLocalTime(schedule.start_date) : ''
            const end = schedule.end_date ? this.toLocalTime(schedule.end_date) : ''
            return `${start} - ${end}`
        },
        // Convert a bar's pixel geometry back into start/end Date objects
        datesForGeometry(left, width) {
            const dayMovement = this.weekColWidth / 7
            const base = new Date(this.calendarStartDate)
            const startOffsetDays = Math.round(left / dayMovement)
            const durationDays = Math.round(width / dayMovement)
            const start = new Date(base)
            start.setDate(start.getDate() + startOffsetDays)
            start.setHours(0, 0, 0, 0)
            const end = new Date(start)
            end.setDate(end.getDate() + durationDays)
            end.setHours(22, 0, 0, 0)
            return { start, end }
        },
        getRelativeX(e) {
            const rect = e.currentTarget.getBoundingClientRect()
            return e.clientX - rect.left
        },
        // Find which bar is under x and whether near an edge (start/end) or the middle
        hitTest(x) {
            const limit = 8
            // Iterate top-most first (last painted = highest z-index)
            for (let i = this.bars.length - 1; i >= 0; i--) {
                const bar = this.bars[i]
                const barEnd = bar.left + bar.width
                if (x >= bar.left && x <= barEnd) {
                    let mode = this.DRAG
                    if (x < bar.left + limit) {
                        mode = this.RESIZE_START
                    } else if (x > barEnd - limit) {
                        mode = this.RESIZE_END
                    }
                    return { id: bar.id, mode, left: bar.left, width: bar.width }
                }
            }
            return null
        },
        containerMouseMove(e) {
            const x = this.getRelativeX(e)
            if (this.resizing) {
                this.resizeActive(x)
            } else if (this.dragging) {
                this.dragActive(e, x)
            } else {
                // Hover feedback only (cursor)
                const hit = this.hitTest(x)
                this.hoverMode = hit ? hit.mode : this.NONE
            }
        },
        containerMouseDown(e) {
            const x = this.getRelativeX(e)
            const hit = this.hitTest(x)
            if (!hit) {
                return
            }
            this.activeId = hit.id
            this.override = { id: hit.id, left: hit.left, width: hit.width }
            this.hoverMode = hit.mode
            this.lastX = x
            this.datesUpdated = false
            if (hit.mode === this.DRAG) {
                this.dragging = true
            } else {
                this.resizing = true
            }
        },
        dragActive(e, x) {
            let moved = x - this.lastX
            const dayMovement = this.weekColWidth / 7
            if (Math.abs(moved) > dayMovement) {
                this.lastX = x
                e.preventDefault()
                // Snap movement to whole days
                const dayDelta = Math.round(moved / dayMovement)
                this.override.left += dayDelta * dayMovement
                this.datesUpdated = true
            }
        },
        resizeActive(x) {
            let moved = x - this.lastX
            const dayMovement = this.weekColWidth / 7
            if (Math.abs(moved) <= dayMovement) {
                return
            }
            this.lastX = x
            const dayDelta = Math.round(moved / dayMovement)
            const px = dayDelta * dayMovement
            if (this.hoverMode === this.RESIZE_START) {
                const newLeft = this.override.left + px
                const newWidth = this.override.width - px
                if (newWidth < dayMovement) {
                    return
                }
                this.override.left = newLeft
                this.override.width = newWidth
                this.datesUpdated = true
            } else if (this.hoverMode === this.RESIZE_END) {
                const newWidth = this.override.width + px
                if (newWidth < dayMovement) {
                    return
                }
                this.override.width = newWidth
                this.datesUpdated = true
            }
        },
        containerMouseUp() {
            if ((this.dragging || this.resizing) && this.datesUpdated && this.override) {
                const { start, end } = this.datesForGeometry(this.override.left, this.override.width)
                this.$emit('dates-changed', this.activeId, start, end)
                // Keep the override in place until the parent's schedules prop updates
                // with the saved value, so the bar doesn't snap back to its old position
                // during the PUT round-trip. The schedules watcher clears it on reconcile.
            } else {
                this.override = null
                this.activeId = null
            }
            this.dragging = false
            this.resizing = false
            this.datesUpdated = false
            this.hoverMode = this.NONE
        },
        containerMouseOut() {
            if (this.dragging || this.resizing) {
                this.containerMouseUp()
            }
        }
    }
}
</script>

<style scoped>
.user-timeline {
    user-select: none;
    position: relative;
}

.week-item {
    display: inline-block;
    color: #FFFFFF;
    background: #ffffff;
    border-top: 1px #BCBCBC solid;
    border-right: 1px #BCBCBC solid;
    margin: 0;
    padding: .1em;
    font-size: .9em;
    user-select: none;
    min-height: 2em;
}

/* Covers the week grid exactly and clips bars to the row so they can't
   bleed above into the user names (bars with dates before the calendar
   start would otherwise have a negative left offset). */
.bars-layer {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    overflow: hidden;
    /* enable mouse interaction for dragging/resizing bars */
    pointer-events: auto;
}

.bars-layer.resize {
    cursor: e-resize;
}

.bars-layer.dragging {
    cursor: grab;
}

.schedule-bar {
    position: absolute;
    top: 2px;
    height: 26px;
    border-radius: .3em;
    /* Semi-transparent so overlapping bars visually darken where they overlap */
    opacity: 0.75;
    /* z-index set inline per bar so the later-starting bar stacks on top */
    pointer-events: auto;
}

.schedule-bar:hover {
    opacity: 0.9;
}

.start-date-text, .end-date-text {
    display: inline-block;
    position: absolute;
    top: 0;
    left: 0;
    max-width: 50%;
    font-size: .8em;
    color: #FFFFFF;
    font-weight: bold;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: left;
    pointer-events: none;
    padding-right: .5em;
    padding-left: .2em;
    margin: 0;
    user-select: none;
}

.end-date-text {
    text-align: right;
    left: auto;
    right: 0;
    padding-left: .5em;
    padding-right: .2em;
}
</style>
